import React, { FormEvent, useState } from "react";
import { Copy, LoaderCircle, RotateCcw } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { showErrorToast, showSuccessToast } from "../../../toastmsg";
import { app, auth } from "../../../firebase";

type GeneratePasswordResetLinkResponse = {
  success: boolean;
  link: string;
};

const ManualResetPassword = () => {
  const [studentEmail, setStudentEmail] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateLink = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = studentEmail.trim().toLowerCase();
    if (!email) {
      showErrorToast("Please enter the student's email.");
      return;
    }

    setIsGenerating(true);
    setResetLink("");

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Your session has expired. Please sign in again.");
      }

      // Refresh before the callable request so Cloud Run never receives a
      // stale Firebase ID token from a long-running dashboard session.
      await currentUser.getIdToken(true);

      const functions = getFunctions(app, "asia-southeast1");
      const generateManualPasswordResetLink = httpsCallable<
        { email: string },
        GeneratePasswordResetLinkResponse
      >(functions, "generateManualPasswordResetLink");

      const result = await generateManualPasswordResetLink({ email });
      const link = result.data.link;

      if (!link) {
        throw new Error("No reset link returned from server.");
      }

      setResetLink(link);
      showSuccessToast("Password reset link generated.");
    } catch (error: any) {
      console.error("Error generating password reset link:", error);
      const errorCode = String(error?.code || "");
      const errorMessage = String(error?.message || "");
      const isAuthenticationError =
        errorCode.includes("unauthenticated") ||
        errorCode.includes("user-token-expired") ||
        errorCode.includes("invalid-user-token") ||
        errorMessage.toLowerCase() === "internal";

      showErrorToast(
        isAuthenticationError
          ? "Your session could not be verified. Please sign out and sign in again."
          : errorMessage || "Failed to generate password reset link.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!resetLink) return;

    try {
      await navigator.clipboard.writeText(resetLink);
      showSuccessToast("Reset link copied to clipboard.");
    } catch (error) {
      console.error("Error copying reset link:", error);
      showErrorToast("Failed to copy reset link.");
    }
  };

  return (
    <div className="max-w-3xl pb-8">
      <div className="mb-6">
        <h2 className="text-lg font-bold">Manual Password Reset</h2>
        <p className="mt-1 text-sm text-gray-600">
          Generate a Firebase password reset link for a student account.
        </p>
      </div>

      <form
        onSubmit={handleGenerateLink}
        className="flex flex-col gap-4 rounded border bg-white p-5 shadow-sm"
      >
        <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
          Student Email
          <input
            type="email"
            value={studentEmail}
            onChange={(event) => setStudentEmail(event.target.value)}
            placeholder="student@example.com"
            className="w-full rounded border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-600"
            disabled={isGenerating}
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={isGenerating}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {isGenerating ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            Generate Link
          </button>

          {resetLink && (
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <Copy className="h-4 w-4" />
              Copy Link
            </button>
          )}
        </div>
      </form>

      {resetLink && (
        <div className="mt-5 rounded border bg-gray-50 p-4">
          <p className="mb-2 text-sm font-medium text-gray-700">
            Password Reset Link
          </p>
          <textarea
            value={resetLink}
            readOnly
            className="min-h-28 w-full resize-y rounded border border-gray-300 bg-white p-3 text-sm text-gray-700 outline-none"
          />
        </div>
      )}
    </div>
  );
};

export default ManualResetPassword;
